import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom/cjs/react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Form, InputGroup } from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import * as helpers from '../../Utils/Helpers';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  atualizarPromotor,
  buscarPromotor,
  inserirPromotor,
} from '~/Services/Service';
import Loading from '../Loading';
import { Selector } from '../Prospectos/ProspectoDetalhes/Pesquisa/Form';

const ModalAdicionarPromotor = ({ location, history, onClose }) => {
  const params = new URLSearchParams(location.search);
  const show = !!params.get('novo') || !!params.get('codigo_usuario');
  if (!show) return null;
  const user = useSelector(state => state.auth).user;

  const handleClose = () => {
    history.push({ search: '' });
    onClose();
  };

  const corretores = useSelector(state => state.corretor);

  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promotor, setPromotor] = useState({});
  const isEdicao = !!params.get('codigo_usuario');

  const fetchPromotor = useCallback(async () => {
    try {
      setLoading(true);
      const response = await buscarPromotor(params.get('codigo_usuario'));
      setPromotor(response);
    } catch (err) {
      console.error(err);
      toast.error('Falha ao carregar promotor. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  const isCorretor = corretores.corretoresById[user.usuario];

  const corretoresOptions = isCorretor
    ? [
        {
          label: user.nome,
          value: user.usuario,
        },
      ]
    : useMemo(
        () =>
          (corretores.listaCorretores || []).map(item => ({
            label: item.nome,
            value: item.codigo,
          })),
        [corretores.listaCorretores]
      );

  useEffect(() => {
    if (params.get('codigo_usuario')) {
      fetchPromotor();
    }
  }, [fetchPromotor]);

  const schema = Yup.object().shape({
    codigo_corretor: Yup.number().nullable(),
    nome_completo: Yup.string().required('Campo obrigatório'),
    nome_usuario: Yup.string().required('Campo obrigatório'),
    email: Yup.string()
      .email('Email inválido')
      .required('Campo obrigatório'),
    cpf: Yup.string()
      .required('Campo obrigatório')
      .test('cpf', 'CPF inválido', value => {
        if (!value) return true;
        return helpers.validarCPF(value);
      }),
    cel: Yup.string()
      .matches(/^(?:\(\d{2}\)\s?)?9\d{4}-\d{4}$/, 'Número de telefone inválido')
      .required('Campo obrigatório'),
    senha: isEdicao ? Yup.string() : Yup.string().required('Campo obrigatório'),
  });

  const initialValues = {
    codigo_corretor: isCorretor
      ? user.usuario
      : promotor.codigo_corretor || undefined,
    nome_completo: promotor.nome || '',
    nome_usuario: promotor.nome_usuario || '',
    email: promotor.email || '',
    cpf: promotor.cpf_usuario || '',
    cel: helpers.format.celular(promotor.cel) || '',
    senha: '',
  };

  const onSubmit = async values => {
    try {
      if (isEdicao) {
        await atualizarPromotor({
          codigo_corretor: isCorretor ? user.usuario : values.codigo_corretor,
          codigo_usuario: promotor.codigo_usuario,
          nome_completo: values.nome_completo,
          telefone: helpers.cleanTelefone(values.cel),
          email: values.email,
        });
      } else {
        await inserirPromotor({
          ...values,
          cpf: helpers.cleanCPF(values.cpf),
          cel: helpers.cleanTelefone(values.cel),
        });
      }
      toast.success(
        `Promotor ${promotor ? 'atualizado' : 'adicionado'} com sucesso`
      );
      handleClose();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) return <Loading />;

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {({
        values,
        handleChange,
        handleBlur,
        errors,
        touched,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
      }) => {
        return (
          <Modal show={true} onHide={handleClose} centered>
            <Modal.Header closeButton>
              <Modal.Title>Novo Indicador</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group>
                  <Selector
                    label="Corretor"
                    noOptionsMessage={() => 'Nenhum corretor encontrado'}
                    placeholder="Pesquisar corretores..."
                    name="codigo_corretor"
                    options={corretoresOptions}
                    onChange={selectedOption => {
                      setFieldValue(
                        'codigo_corretor',
                        selectedOption ? selectedOption.value : ''
                      );
                      setFieldTouched('codigo_corretor', true, true);
                    }}
                    onBlur={() =>
                      setFieldTouched('codigo_corretor', true, true)
                    }
                    value={
                      corretoresOptions.find(
                        opt =>
                          opt.value ===
                          (isCorretor ? user.usuario : values.codigo_corretor)
                      ) || null
                    }
                  />
                  {touched.codigo_corretor && !!errors.codigo_corretor && (
                    <div className="invalid-feedback d-block">
                      {errors.codigo_corretor}
                    </div>
                  )}
                </Form.Group>
                <Form.Group>
                  <Form.Label>Nome Completo</Form.Label>
                  <Form.Control
                    type="text"
                    name="nome_completo"
                    placeholder="Informe o nome completo"
                    value={values.nome_completo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.nome_completo && !!errors.nome_completo}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.nome_completo}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Nome de Usuário</Form.Label>
                  <Form.Control
                    type="text"
                    name="nome_usuario"
                    placeholder="Informe o nome de usuário"
                    autoComplete="off"
                    value={values.nome_usuario}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.nome_usuario && !!errors.nome_usuario}
                    disabled={isEdicao}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.nome_usuario}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Telefone</Form.Label>
                  <MaskedInput
                    guide={false}
                    showMask={false}
                    type="tel"
                    className="form-control"
                    name="cel"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.cel}
                    mask={helpers.mask.celular}
                    placeholder="(00) 0000-0000"
                  />
                  {touched.cel && errors.cel && (
                    <small className="form-text text-danger">
                      {errors.cel}
                    </small>
                  )}
                </Form.Group>
                <Form.Group>
                  <Form.Label>E-mail</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Informe o e-mail"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && !!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>CPF</Form.Label>
                  <MaskedInput
                    guide={false}
                    showMask={false}
                    type="text"
                    className="form-control"
                    name="cpf"
                    inputMode="numeric"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.cpf}
                    mask={helpers.mask.cpf_cnpj}
                    disabled={isEdicao}
                    placeholder="000.000.000-00"
                  />
                  {touched.cpf && errors.cpf && (
                    <small className="form-text text-danger">
                      {errors.cpf}
                    </small>
                  )}
                </Form.Group>
                <Form.Group>
                  <Form.Label>Senha</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={mostrarSenha ? 'text' : 'password'}
                      name="senha"
                      placeholder="Informe a senha"
                      maxLength={30}
                      value={values.senha}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="new-password"
                      isInvalid={touched.senha && !!errors.senha}
                      disabled={isEdicao}
                    />
                    <Button
                      disabled={isEdicao}
                      onClick={() => setMostrarSenha(!mostrarSenha)}
                      variant="outline-primary"
                    >
                      {!mostrarSenha ? <FaEye /> : <FaEyeSlash />}
                    </Button>
                  </InputGroup>
                  <Form.Control.Feedback type="invalid">
                    {errors.senha}
                  </Form.Control.Feedback>
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="link" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                disabled={isSubmitting}
                variant="primary"
                type="submit"
                onClick={handleSubmit}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </Modal.Footer>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default withRouter(connect()(ModalAdicionarPromotor));
